import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { FixedPriceOverviewDto, FixedPriceTypeVariant } from '../../../config/registrationConfig'

import ViewRegistrationRow, {
    ViewRegistrationColumnProps,
    ViewRegistrationColumns,
    ViewRegistrationRowProps,
} from '../Registration/ViewRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { multiplyRate, parseNumber } from '../../../helpers/numberHelper'
import { Stack, StackItem } from '@fluentui/react'
import { FixedPriceListProps } from './FixedPriceList'
import useVatType from '../../../hooks/useVatType'
import useFixedPriceType from '../../../hooks/useFixedPriceType'
import useFixedPriceTypes from '../../../hooks/useFixedPriceTypes'
import { MoneyDto } from '../../../config/i18nConfig'
import { getOppositePrice } from '../../../helpers/priceHelper'
import { PricePrecedence } from '../../../config/finance/financeConfig'
import { Text, Tooltip } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { RichText } from '../../../v2/components/Text/RichText'

type Props = ViewRegistrationRowProps<FixedPriceOverviewDto, FixedPriceListProps>
type ColumnProps = ViewRegistrationColumnProps<FixedPriceOverviewDto, FixedPriceListProps>

function Columns(props: ColumnProps): ReactElement {
    const { t: translate } = useTranslation()

    const { vatType } = useVatType(props.item.vatType)
    const [fixedPriceType] = useFixedPriceType(props.item.type)
    const { fixedPriceTypes } = useFixedPriceTypes()

    const vatValue = useMemo(() => vatType !== undefined && multiplyRate(vatType.percentage / 100, props.item.rate), [props.item])
    const totalValue = useMemo<MoneyDto>(
        () => ({
            ...props.item.rate,
            amount: `${getOppositePrice(parseNumber(props.item.rate.amount, 'en'), vatType, PricePrecedence.Subtotal)}`,
        }),
        [props.item],
    )

    const useRateFields = useMemo<boolean>(() => ![FixedPriceTypeVariant.Mock].includes(fixedPriceType?.variant), [fixedPriceTypes])

    switch (props.column.key) {
        case 'type':
            return fixedPriceType !== undefined && <Text size={200}>{fixedPriceType?.name}</Text>
        case 'description': {
            // The long description section only appears when there is one; the name alone already fills the tooltip.
            const longDescription = props.item.longDescription?.trim()

            return (
                <Tooltip
                    relationship={'description'}
                    withArrow
                    content={
                        <div>
                            <Text size={200} weight="semibold">
                                {translate('fixedprice.name')}:{' '}
                            </Text>
                            <Text size={200}>{props.item.name}</Text>
                            {longDescription && (
                                <>
                                    <br />
                                    <Text size={200} weight="semibold">
                                        {translate('fixedprice.longdescription')}:{' '}
                                    </Text>
                                    <Text size={200}>
                                        <RichText content={longDescription} />
                                    </Text>
                                </>
                            )}
                        </div>
                    }
                >
                    <span>
                        <Text size={200}>{props.item.description}</Text>
                    </span>
                </Tooltip>
            )
        }
        case 'rate':
            return useRateFields && props.item.rate !== undefined && <MoneyText value={props.item.rate} />
        case 'vat':
            return (
                useRateFields &&
                vatValue && (
                    <Stack horizontal tokens={{ childrenGap: 0 }}>
                        <StackItem grow>
                            <Text size={200}>
                                <MoneyText value={vatValue} align="left" />
                            </Text>
                        </StackItem>
                        <StackItem>
                            <Text size={200}>{vatType.percentage}%</Text>
                        </StackItem>
                    </Stack>
                )
            )
        case 'total':
            return useRateFields && totalValue && <MoneyText value={totalValue} align="right" />
    }

    return <ViewRegistrationColumns<FixedPriceOverviewDto, FixedPriceListProps> {...props} />
}

export default function ViewFixedPriceRow(props: Props): ReactElement {
    return <ViewRegistrationRow<FixedPriceOverviewDto, FixedPriceListProps> {...props} columns={Columns} />
}
